import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, MessageSquare, CalendarPlus, RefreshCw, History, FileText } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { SmsDialog } from "./SmsDialog";
import { ScheduleDialog } from "./ScheduleDialog";
import { agreementLink, buildSchedulingLinkSms, firstName, fmtDate, smsHref } from "./leadUtils";
import { useConfig } from "../../hooks/useConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0a0a0a] border-zinc-800 text-white h-10 rounded-sm placeholder:text-zinc-600 text-sm";

const RecurringForm = ({ customer, onDone }) => {
  const [form, setForm] = useState({ service_type: "", vehicle_info: "", last_service_date: "", next_service_date: "", notes: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!form.service_type || !form.next_service_date) return toast.error("Service type and next service date are required.");
    try {
      await axios.post(`${API}/admin/recurring`, { ...form, customer_id: customer.customer_id });
      toast.success("Recurring reminder created — see Recurring tab");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create reminder");
    }
  };
  return (
    <div className="grid grid-cols-2 gap-2 bg-[#0a0a0a] border border-zinc-800 rounded-sm p-3">
      <Input data-testid="recurring-form-service" className={inputCls} placeholder="Service type (e.g. Oil Change) *" value={form.service_type} onChange={set("service_type")} />
      <Input data-testid="recurring-form-vehicle" className={inputCls} placeholder="Vehicle (e.g. 2019 Camry)" value={form.vehicle_info} onChange={set("vehicle_info")} />
      <div>
        <div className="text-zinc-600 text-[10px] mb-0.5">Last service date</div>
        <Input data-testid="recurring-form-last" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.last_service_date} onChange={set("last_service_date")} />
      </div>
      <div>
        <div className="text-zinc-600 text-[10px] mb-0.5">Next service due *</div>
        <Input data-testid="recurring-form-next" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.next_service_date} onChange={set("next_service_date")} />
      </div>
      <Input className={`${inputCls} col-span-2`} placeholder="Notes" value={form.notes} onChange={set("notes")} />
      <button data-testid="recurring-form-save" onClick={save} className="col-span-2 bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-4 py-2 text-xs font-semibold">
        Create Recurring Reminder
      </button>
    </div>
  );
};

export const CustomerManageDialog = ({ customer, onClose, onChanged }) => {
  const config = useConfig();
  const [notes, setNotes] = useState(customer.notes || "");
  const [smsOpen, setSmsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [forms, setForms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/customers/${customer.customer_id}/forms`)
      .then((r) => setForms(r.data))
      .catch(() => setForms([]));
  }, [customer.customer_id]);

  const patch = async (updates, msg) => {
    try {
      await axios.patch(`${API}/admin/customers/${customer.customer_id}`, updates);
      toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const logSms = async (message) => {
    try {
      await axios.post(`${API}/admin/customers/${customer.customer_id}/contact-log`, { message, channel: "sms" });
      toast.success("Message logged to customer history");
      onChanged();
    } catch {
      toast.error("Could not log message");
    }
  };

  const btn = "inline-flex items-center gap-1.5 border rounded-sm px-3 h-9 text-xs font-semibold transition-colors";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="customer-manage-dialog">
        <DialogHeader><DialogTitle className="font-heading">{customer.name}</DialogTitle></DialogHeader>
        <div className="text-sm text-zinc-400 -mt-2 space-y-0.5">
          <div>{customer.phone}{customer.email ? ` · ${customer.email}` : ""}</div>
          {customer.address && <div>{customer.address}</div>}
          {customer.referral_source && <div className="text-zinc-500 text-xs">Heard about us: {customer.referral_source}</div>}
          <div className="text-zinc-600 text-xs">
            Requested: {(customer.services_requested || []).join(", ") || "—"} · Completed: {(customer.services_completed || []).join(", ") || "—"}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-t border-zinc-800 pt-4">
          <a data-testid="customer-action-call" href={`tel:${customer.phone.replace(/[^+\d]/g, "")}`} className={`${btn} border-blue-500/50 text-blue-400 hover:bg-blue-500/10`}><PhoneCall className="h-3.5 w-3.5" /> Call</a>
          <a data-testid="customer-action-text" href={smsHref(customer.phone, `Hi ${firstName(customer.name)}, this is Mason's Mobile Mechanic. `)} className={`${btn} border-blue-500/50 text-blue-400 hover:bg-blue-500/10`}><MessageSquare className="h-3.5 w-3.5" /> Text</a>
          <button data-testid="customer-action-schedule" onClick={() => setScheduleOpen(true)} className={`${btn} border-blue-500/50 text-blue-400 hover:bg-blue-500/10`}><CalendarPlus className="h-3.5 w-3.5" /> Schedule Appointment</button>
          <button data-testid="customer-action-send-link" onClick={() => setSmsOpen(true)} className={`${btn} border-blue-500/50 text-blue-400 hover:bg-blue-500/10`}><MessageSquare className="h-3.5 w-3.5" /> Send Scheduling Link</button>
          <button data-testid="customer-action-recurring" onClick={() => setRecurringOpen((v) => !v)} className={`${btn} border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10`}><RefreshCw className="h-3.5 w-3.5" /> Add Recurring Reminder</button>
        </div>

        {recurringOpen && <RecurringForm customer={customer} onDone={() => { setRecurringOpen(false); onChanged(); }} />}

        <div className="border-t border-zinc-800 pt-4 space-y-2">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Customer Notes</div>
          <Textarea data-testid="customer-notes-input" className="bg-[#0a0a0a] border-zinc-800 text-white rounded-sm h-24 text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button data-testid="customer-notes-save" onClick={() => patch({ notes }, "Notes saved")} className={`${btn} border-zinc-700 text-zinc-300 hover:border-zinc-500`}>Save Notes</button>
        </div>

        <div className="border-t border-zinc-800 pt-4">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> Form Submissions</div>
          {forms === null ? (
            <p className="text-zinc-600 text-xs">Loading…</p>
          ) : forms.length === 0 ? (
            <p className="text-zinc-600 text-xs">No website form submissions found for this customer's phone or email.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="customer-forms-list">
              {forms.map((f) => (
                <div key={f.id} data-testid={`customer-form-${f.id}`} className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-3 space-y-1">
                  <div className="text-zinc-500 text-[11px]">Submitted {fmtDate(f.created_at)}</div>
                  <div className="text-white text-xs font-semibold">{f.service_needed} — {f.vehicle_year} {f.vehicle_make} {f.vehicle_model}</div>
                  {f.preferred_date && <div className="text-zinc-400 text-xs">Preferred date: {f.preferred_date}</div>}
                  {f.referral_source && <div className="text-zinc-500 text-xs">Heard about us: {f.referral_source}</div>}
                  {f.message && <p className="text-zinc-300 text-xs leading-relaxed whitespace-pre-wrap border-t border-zinc-800/70 pt-1.5 mt-1.5">{f.message}</p>}
                  <div className={`text-[10px] ${f.terms_accepted ? "text-emerald-400" : "text-amber-400"}`}>{f.terms_accepted ? `Terms accepted ${f.terms_accepted_at ? fmtDate(f.terms_accepted_at) : ""} (v${f.terms_version || "1.0"})` : "Terms not accepted"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-zinc-800 pt-4">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-2"><History className="h-3.5 w-3.5" /> Contact History</div>
          {(customer.contact_history || []).length === 0 ? (
            <p className="text-zinc-600 text-xs">No messages logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {[...customer.contact_history].reverse().map((h, i) => (
                <div key={`${h.sent_at}-${i}`} className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-2.5">
                  <div className="text-zinc-600 text-[10px] uppercase mb-1">{h.channel} · {fmtDate(h.sent_at)}</div>
                  <div className="text-zinc-300 text-xs">{h.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {smsOpen && (
          <SmsDialog
            key={`cust-sms-${customer.customer_id}`}
            open={smsOpen}
            onOpenChange={setSmsOpen}
            phone={customer.phone}
            title="Send Scheduling Link"
            initialMessage={buildSchedulingLinkSms(firstName(customer.name), config.booking_url || "(booking link not configured)", agreementLink(customer.lead_id))}
            onLogged={logSms}
          />
        )}
        {scheduleOpen && (
          <ScheduleDialog
            key={`cust-sched-${customer.customer_id}`}
            open={scheduleOpen}
            onOpenChange={setScheduleOpen}
            context={{ customer_id: customer.customer_id, name: customer.name, phone: customer.phone, address: customer.address }}
            onSaved={() => { onChanged(); toast.info("Appointment visible in the Appointments tab"); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
